import * as React from 'react';
import { useCallback } from 'react';

import { RewardsBadge, VerticalCarouselItem } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';

import Picture from 'components/picture';
import { ResponsiveCarouselSkeletonItem } from 'components/responsive-carousel/responsive-carousel.skeleton';
import {
  makeLoyaltyRewardDetailsPath,
  makeUrlForIncentive,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/utils';
import { CustomEventNames, logLoyaltyEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { useServiceModeLive } from 'state/service-mode';
import { getDesignToken } from 'styles/constants';
import { maybeLocale } from 'utils/graphql';
import logger from 'utils/logger';

import { IRewardsCarouselCardProps } from './types';

export const RewardsCarouselCard = React.memo<IRewardsCarouselCardProps>(({ reward }) => {
  const image = maybeLocale(reward.image);
  const name = reward?.name?.locale || '';
  const { noStoreSelected, store } = useStoreContext();

  const { navigate } = useRouter();
  const serviceMode = useServiceModeLive();
  const { isInRestaurantRedemptionEnabledAtStore } = useInRestaurantRedemptionContext();

  const handleRewardClick = useCallback(() => {
    async function navigateToReward() {
      const rewardWizardUrl = makeUrlForIncentive(reward);
      if (!rewardWizardUrl) {
        logger.warn(
          `Attempting to make a url for reward that is missing data: reward ${reward._id}`
        );
        return;
      }
      const indexOfSearch = rewardWizardUrl.indexOf('?');
      const search = indexOfSearch !== -1 ? rewardWizardUrl.slice(indexOfSearch) : '';

      const shouldNavigateToRewardsDetails =
        reward?._id && (await isInRestaurantRedemptionEnabledAtStore(store, serviceMode));

      if (shouldNavigateToRewardsDetails) {
        navigate(makeLoyaltyRewardDetailsPath({ rewardId: reward._id, search }));
        logLoyaltyEvent({
          name: CustomEventNames.REWARD_SELECTED,
          incentives: [reward],
        });
      } else {
        navigate(rewardWizardUrl);
      }
    }
    navigateToReward();
  }, [
    isInRestaurantRedemptionEnabledAtStore,
    navigate,
    noStoreSelected,
    reward,
    serviceMode,
    store,
  ]);

  if (!reward.incentives?.length) {
    return <ResponsiveCarouselSkeletonItem />;
  }

  return (
    <VerticalCarouselItem
      mr="$0"
      header={name}
      image={
        image && (
          <Picture
            objectFitContain
            image={image}
            alt={reward?.imageDescription?.locale || reward?.name?.locale || ''}
            width="126px"
            alignSelf="center"
          />
        )
      }
      variant="rewards"
      disabled={reward.locked}
      unavailable={!reward.isAvailabilityLoading && !reward.isAvailable}
      unavailableMessage={t('notAvailableAtStore')}
      rewardsBadge={
        <RewardsBadge
          isLocked={reward.locked}
          color={getDesignToken('crown-color')}
          accessibilityLabel={
            reward.locked
              ? `${reward.pointCost.toString()} ${t('crownPoints')}, ${t('locked')}`
              : `${reward.pointCost.toString()} ${t('crownPoints')}`
          }
        >
          {reward.pointCost.toString()}
        </RewardsBadge>
      }
      onPress={handleRewardClick}
    />
  );
});
